import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Loader2, ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatError(detail) {
  if (!detail) return "Errore imprevisto. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const inputClass =
  "w-full bg-[#0A0E14] border border-white/15 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00C8D7] transition-colors";

export default function AdminLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login"); // login | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Accesso effettuato.");
      nav("/admin");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E14] tech-grid-bg flex items-center justify-center px-6 text-white">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
        <button data-testid="admin-back-home" onClick={() => nav("/")} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm font-mono-tech mb-8 transition-colors">
          <ArrowLeft size={16} /> Torna al sito
        </button>

        <div className="bg-[#111827] border border-white/10 p-8 md:p-10">
          <div className="flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="copypro3d.it" className="h-12 w-12 rounded-full object-cover" />
            <div>
              <h1 className="font-display font-bold text-xl tracking-tight">Area Richieste</h1>
              <span className="font-mono-tech text-xs text-slate-500">
                {mode === "login" ? "Accesso riservato" : "Recupero password"}
              </span>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {mode === "login" ? (
              <motion.form key="login" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onSubmit={onLogin} data-testid="admin-login-form" className="space-y-4">
                <input data-testid="admin-email" type="email" className={inputClass} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
                <input data-testid="admin-password" type="password" className={inputClass} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
                <button data-testid="admin-login-submit" type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-[#00C8D7] text-[#0A0E14] font-bold px-8 py-3.5 hover:bg-[#00C8D7]/85 transition-colors disabled:opacity-60">
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <Lock size={18} />}
                  Accedi
                </button>
                <button type="button" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setForgotSent(false); }} className="w-full text-center font-mono-tech text-xs text-slate-500 hover:text-[#00C8D7] transition-colors pt-1">
                  Password dimenticata?
                </button>
              </motion.form>
            ) : (
              <motion.div key="forgot" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                {forgotSent ? (
                  <div data-testid="forgot-sent" className="text-center py-4">
                    <CheckCircle2 size={40} className="text-[#00C8D7] mx-auto" />
                    <p className="text-slate-300 mt-4 leading-relaxed">
                      Se l'email è registrata, riceverai un link per reimpostare la password. Controlla la tua casella.
                    </p>
                    <button data-testid="back-to-login" onClick={() => setMode("login")} className="mt-6 border border-[#00C8D7] text-[#00C8D7] font-bold px-6 py-2.5 hover:bg-[#00C8D7]/10 transition-colors">
                      Torna al login
                    </button>
                  </div>
                ) : (
                  <form onSubmit={onForgot} data-testid="forgot-form" className="space-y-4">
                    <p className="text-sm text-slate-400 leading-relaxed">
                      Inserisci la tua email: ti invieremo un link per reimpostare la password.
                    </p>
                    <input data-testid="forgot-email" type="email" className={inputClass} placeholder="Email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} />
                    <button data-testid="forgot-submit" type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-[#00C8D7] text-[#0A0E14] font-bold px-8 py-3.5 hover:bg-[#00C8D7]/85 transition-colors disabled:opacity-60">
                      {loading ? <Loader2 size={18} className="animate-spin" /> : <Mail size={18} />}
                      Invia link di recupero
                    </button>
                    <button type="button" data-testid="cancel-forgot" onClick={() => setMode("login")} className="w-full text-center font-mono-tech text-xs text-slate-500 hover:text-white transition-colors pt-1">
                      Annulla
                    </button>
                  </form>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
